import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Ideas } from "@/api/resources";
import type { IdeaOut } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { TextArea } from "@/components/ui/Field";
import { useToast } from "@/components/ui/Toast";

/** Only rendered for the author; the server checks ownership and version too. */
export function WithdrawIdea({ idea, onDone }: { idea: IdeaOut; onDone?: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const toast = useToast();
  const qc = useQueryClient();
  const withdraw = useMutation({
    mutationFn: () => Ideas.withdraw(idea.id, reason.trim(), idea.version),
    onSuccess: () => {
      toast.success("Идея отозвана. Обсуждение и файлы сохранены.");
      setOpen(false);
      onDone?.();
    },
    onError: (error: Error) => toast.error(error.message),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["ideas"] });
      qc.invalidateQueries({ queryKey: ["memory-stats"] });
    },
  });
  if (idea.status !== "new") {
    return <p className="text-meta text-hint">Ревью уже началось. Решение по идее принимает партнёр.</p>;
  }
  if (!open) return <Button variant="destructive-outline" block onClick={() => setOpen(true)}>Отозвать идею</Button>;
  return (
    <div className="space-y-2">
      <TextArea label="Причина отзыва" value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="Почему ты решил отозвать идею?" rows={3} autoFocus disabled={withdraw.isPending} />
      <p className="text-meta text-hint">Идея останется в истории с пометкой «отозвана автором».</p>
      {withdraw.isError && <p role="alert" className="text-meta text-dstr">{withdraw.error.message}</p>}
      <div className="flex gap-2">
        <Button variant="destructive" loading={withdraw.isPending} disabled={!reason.trim()} onClick={() => withdraw.mutate()}>Подтвердить отзыв</Button>
        <Button variant="secondary" disabled={withdraw.isPending} onClick={() => setOpen(false)}>Отмена</Button>
      </div>
    </div>
  );
}
